import {
  computeWorldUpFaceBasis,
  type FaceBasis,
  type Vec3,
} from "./faceBasis.ts";
import {
  type AdjacencyCache,
  edgeFaces,
  edgeVertices,
  getAdjacencyCache,
  numEdgeKey,
} from "./topology.ts";
import type { PolygonTopology } from "./types.ts";
import type { ViewportSurfaceHit } from "./viewportSurfaceRaycast.ts";
import { buildWeldReps } from "./weld.ts";

const NORMAL_EPSILON = 1e-6;
const EDGE_EPSILON = 1e-5;
const COPLANAR_NORMAL_DOT = 0.999;
const REGION_NORMAL_DOT = 0.999;
const REGION_PLANE_EPSILON = 1e-4;

type SurfaceMesh = NonNullable<NonNullable<ViewportSurfaceHit["face"]>["mesh"]>;

function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function scale(v: Vec3, s: number): Vec3 {
  return [v[0] * s, v[1] * s, v[2] * s];
}

function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function lengthSq(v: Vec3): number {
  return dot(v, v);
}

function normalize(v: Vec3): Vec3 | null {
  const len2 = lengthSq(v);
  if (len2 < NORMAL_EPSILON * NORMAL_EPSILON) return null;
  return scale(v, 1 / Math.sqrt(len2));
}

function distanceSq(a: Vec3, b: Vec3): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}

function pointSegmentDistanceSq(point: Vec3, a: Vec3, b: Vec3): number {
  const ab = sub(b, a);
  const denom = lengthSq(ab);
  if (denom < NORMAL_EPSILON * NORMAL_EPSILON) return distanceSq(point, a);
  const t = Math.max(0, Math.min(1, dot(sub(point, a), ab) / denom));
  return distanceSq(point, add(a, scale(ab, t)));
}

function verticesScale(vertices: readonly Vec3[]): number {
  let best = 1;
  for (let i = 0; i < vertices.length; i += 1) {
    for (let j = i + 1; j < vertices.length; j += 1) {
      best = Math.max(best, Math.sqrt(distanceSq(vertices[i]!, vertices[j]!)));
    }
  }
  return best;
}

export function surfaceHitBasis(hit: ViewportSurfaceHit): FaceBasis | null {
  const normal = surfaceHitFaceNormal(hit) ?? hit.normal;
  return computeWorldUpFaceBasis({ worldPosition: hit.worldPosition, normal });
}

function localVertex(positions: ArrayLike<number>, vi: number): Vec3 {
  const x = positions[vi * 3]!;
  const y = positions[vi * 3 + 1]!;
  const z = positions[vi * 3 + 2]!;
  return [x, y, z];
}

function transformLocalPoint(local: Vec3, matrix: ArrayLike<number>): Vec3 {
  const x = local[0];
  const y = local[1];
  const z = local[2];
  return [
    matrix[0]! * x + matrix[4]! * y + matrix[8]! * z + matrix[12]!,
    matrix[1]! * x + matrix[5]! * y + matrix[9]! * z + matrix[13]!,
    matrix[2]! * x + matrix[6]! * y + matrix[10]! * z + matrix[14]!,
  ];
}

function meshFaceVertices(hit: ViewportSurfaceHit, faceIndex: number): Vec3[] {
  const mesh = hit.face?.mesh;
  if (!mesh) return [];
  const start = mesh.topology.offsets[faceIndex];
  const end = mesh.topology.offsets[faceIndex + 1];
  if (start === undefined || end === undefined || end <= start) return [];
  const vertices: Vec3[] = [];
  for (let c = start; c < end; c += 1) {
    const vi = mesh.topology.corners[c]!;
    vertices.push(
      transformLocalPoint(localVertex(mesh.positions, vi), mesh.worldMatrix),
    );
  }
  return vertices;
}

function normalFromVertices(vertices: readonly Vec3[]): Vec3 | null {
  if (vertices.length < 3) return null;
  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let i = 0; i < vertices.length; i += 1) {
    const a = vertices[i]!;
    const b = vertices[(i + 1) % vertices.length]!;
    nx += (a[1] - b[1]) * (a[2] + b[2]);
    ny += (a[2] - b[2]) * (a[0] + b[0]);
    nz += (a[0] - b[0]) * (a[1] + b[1]);
  }
  return normalize([nx, ny, nz]);
}

function faceCentroid(vertices: readonly Vec3[]): Vec3 | null {
  if (vertices.length === 0) return null;
  let out: Vec3 = [0, 0, 0];
  for (const vertex of vertices) out = add(out, vertex);
  return scale(out, 1 / vertices.length);
}

function alignNormalToReference(normal: Vec3, reference: Vec3 | null): Vec3 {
  return reference && dot(normal, reference) < 0 ? scale(normal, -1) : normal;
}

function surfaceHitFaceNormal(hit: ViewportSurfaceHit): Vec3 | null {
  const vertices =
    hit.face?.mesh && hit.face.index !== undefined
      ? meshFaceVertices(hit, hit.face.index)
      : hit.face?.vertices;
  if (!vertices || vertices.length < 3) return null;
  const normal = normalFromVertices(vertices);
  const reference = normalize(hit.normal);
  return normal ? alignNormalToReference(normal, reference) : null;
}

interface SurfaceRegion {
  hit: ViewportSurfaceHit;
  faces: Set<number>;
}

interface SurfaceRegionConnectivity {
  cache: AdjacencyCache;
  weldReps: Int32Array | null;
  weldEdgeMap: Map<number, number[]> | null;
  vertexFaceMap: Map<number, number[]>;
}

const connectivityByTopology = new WeakMap<
  PolygonTopology,
  WeakMap<Float32Array, SurfaceRegionConnectivity>
>();

export interface SurfaceCoplanarRegionFace {
  index: number;
  vertices: readonly Vec3[];
}

export interface SurfaceCoplanarRegion {
  objectId: string;
  faceIndices: readonly number[];
  faces: readonly SurfaceCoplanarRegionFace[];
}

function buildWeldEdgeMap(
  topology: PolygonTopology,
  weldReps: Int32Array,
): Map<number, number[]> {
  const out = new Map<number, number[]>();
  const edgeCount = topology.edges.length / 2;
  for (let edgeIndex = 0; edgeIndex < edgeCount; edgeIndex += 1) {
    const a = weldReps[topology.edges[edgeIndex * 2]!]!;
    const b = weldReps[topology.edges[edgeIndex * 2 + 1]!]!;
    const key = numEdgeKey(a, b);
    const edges = out.get(key);
    if (edges) {
      edges.push(edgeIndex);
    } else {
      out.set(key, [edgeIndex]);
    }
  }
  return out;
}

function buildVertexFaceMap(
  topology: PolygonTopology,
  weldReps: Int32Array | null,
): Map<number, number[]> {
  const out = new Map<number, number[]>();
  const faceCount = topology.offsets.length - 1;
  for (let faceIndex = 0; faceIndex < faceCount; faceIndex += 1) {
    const start = topology.offsets[faceIndex]!;
    const end = topology.offsets[faceIndex + 1]!;
    for (let c = start; c < end; c += 1) {
      const vertexIndex = topology.corners[c]!;
      const representative = weldReps?.[vertexIndex] ?? vertexIndex;
      const faces = out.get(representative);
      if (faces) {
        faces.push(faceIndex);
      } else {
        out.set(representative, [faceIndex]);
      }
    }
  }
  return out;
}

function getSurfaceRegionConnectivity(
  mesh: SurfaceMesh,
): SurfaceRegionConnectivity {
  let byPositions = connectivityByTopology.get(mesh.topology);
  if (!byPositions) {
    byPositions = new WeakMap<Float32Array, SurfaceRegionConnectivity>();
    connectivityByTopology.set(mesh.topology, byPositions);
  }

  const cached = byPositions.get(mesh.positions);
  if (cached) return cached;

  const cache = getAdjacencyCache(mesh.topology, mesh.positions.length / 3);
  const weldReps = buildWeldReps(mesh.positions);
  let hasWelds = false;
  for (let i = 0; i < weldReps.length; i += 1) {
    if (weldReps[i] !== i) {
      hasWelds = true;
      break;
    }
  }

  const connectivity: SurfaceRegionConnectivity = {
    cache,
    weldReps: hasWelds ? weldReps : null,
    weldEdgeMap: hasWelds ? buildWeldEdgeMap(mesh.topology, weldReps) : null,
    vertexFaceMap: buildVertexFaceMap(
      mesh.topology,
      hasWelds ? weldReps : null,
    ),
  };
  byPositions.set(mesh.positions, connectivity);
  return connectivity;
}

function edgeFacesIncludingWelds(
  topology: PolygonTopology,
  connectivity: SurfaceRegionConnectivity,
  edgeIndex: number,
): readonly number[] | Uint32Array {
  const { cache, weldEdgeMap, weldReps } = connectivity;
  if (!weldEdgeMap || !weldReps) return edgeFaces(cache, edgeIndex);

  const [v0, v1] = edgeVertices(topology.edges, edgeIndex);
  const equivalentEdges =
    weldEdgeMap.get(numEdgeKey(weldReps[v0]!, weldReps[v1]!)) ?? [];
  if (equivalentEdges.length === 1 && equivalentEdges[0] === edgeIndex) {
    return edgeFaces(cache, edgeIndex);
  }

  const out: number[] = [];
  const seen = new Set<number>();
  for (const equivalentEdge of equivalentEdges) {
    const faces = edgeFaces(cache, equivalentEdge);
    for (const faceIndex of faces) {
      if (seen.has(faceIndex)) continue;
      seen.add(faceIndex);
      out.push(faceIndex);
    }
  }
  return out;
}

function faceNeighborsIncludingWelds(
  topology: PolygonTopology,
  connectivity: SurfaceRegionConnectivity,
  faceIndex: number,
): number[] {
  const start = topology.offsets[faceIndex]!;
  const end = topology.offsets[faceIndex + 1]!;
  const out: number[] = [];
  const seen = new Set<number>();
  seen.add(faceIndex);
  for (let c = start; c < end; c += 1) {
    const edgeIndex = topology.cornerEdges[c]!;
    const faces = edgeFacesIncludingWelds(topology, connectivity, edgeIndex);
    for (const adjacentFace of faces) {
      if (seen.has(adjacentFace)) continue;
      seen.add(adjacentFace);
      out.push(adjacentFace);
    }
  }
  for (let c = start; c < end; c += 1) {
    const vertexIndex = topology.corners[c]!;
    const representative = connectivity.weldReps?.[vertexIndex] ?? vertexIndex;
    const faces = connectivity.vertexFaceMap.get(representative) ?? [];
    for (const adjacentFace of faces) {
      if (seen.has(adjacentFace)) continue;
      seen.add(adjacentFace);
      out.push(adjacentFace);
    }
  }
  return out;
}

function buildCoplanarRegion(hit: ViewportSurfaceHit): SurfaceRegion | null {
  const mesh = hit.face?.mesh;
  const seedFace = hit.face?.index;
  const seedNormal = normalize(hit.normal);
  if (!mesh || seedFace === undefined) return null;

  const seedVertices = meshFaceVertices(hit, seedFace);
  if (seedVertices.length < 3) return null;
  const rawRegionNormal = normalFromVertices(seedVertices) ?? seedNormal;
  if (!rawRegionNormal) return null;
  const regionNormal = alignNormalToReference(rawRegionNormal, seedNormal);
  const planePoint = faceCentroid(seedVertices) ?? hit.worldPosition;
  const planeEpsilon =
    Math.max(verticesScale(seedVertices), 1) * REGION_PLANE_EPSILON;
  const connectivity = getSurfaceRegionConnectivity(mesh);
  const faces = new Set<number>();
  const queue = [seedFace];
  faces.add(seedFace);

  function accepts(faceIndex: number): boolean {
    const vertices = meshFaceVertices(hit, faceIndex);
    if (vertices.length < 3) return false;
    const normal = normalFromVertices(vertices);
    if (!normal || dot(normal, regionNormal) < REGION_NORMAL_DOT) return false;
    for (const vertex of vertices) {
      if (Math.abs(dot(sub(vertex, planePoint), regionNormal)) > planeEpsilon) {
        return false;
      }
    }
    return true;
  }

  for (let read = 0; read < queue.length; read += 1) {
    for (const neighbor of faceNeighborsIncludingWelds(
      mesh.topology,
      connectivity,
      queue[read]!,
    )) {
      if (faces.has(neighbor) || !accepts(neighbor)) continue;
      faces.add(neighbor);
      queue.push(neighbor);
    }
  }

  return { hit, faces };
}

export function coplanarSurfaceRegionFromHit(
  hit: ViewportSurfaceHit,
): SurfaceCoplanarRegion | null {
  const region = buildCoplanarRegion(hit);
  if (!region) return null;

  const faceIndices = [...region.faces].sort((a, b) => a - b);
  const faces: SurfaceCoplanarRegionFace[] = [];
  for (const index of faceIndices) {
    const vertices = meshFaceVertices(hit, index);
    if (vertices.length >= 3) {
      faces.push({ index, vertices });
    }
  }
  if (faces.length === 0) return null;

  return {
    objectId: hit.objectId,
    faceIndices,
    faces,
  };
}

function sharedRegionEdge(
  primary: SurfaceRegion,
  secondary: SurfaceRegion,
): readonly [Vec3, Vec3] | null {
  const primaryMesh = primary.hit.face?.mesh;
  const secondaryMesh = secondary.hit.face?.mesh;
  if (
    !primaryMesh ||
    !secondaryMesh ||
    primaryMesh.topology !== secondaryMesh.topology ||
    primaryMesh.positions !== secondaryMesh.positions
  ) {
    return null;
  }

  const connectivity = getSurfaceRegionConnectivity(primaryMesh);
  let best: readonly [Vec3, Vec3] | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const faceIndex of primary.faces) {
    const start = primaryMesh.topology.offsets[faceIndex]!;
    const end = primaryMesh.topology.offsets[faceIndex + 1]!;
    for (let c = start; c < end; c += 1) {
      const edgeIndex = primaryMesh.topology.cornerEdges[c]!;
      const adjacentFaces = edgeFacesIncludingWelds(
        primaryMesh.topology,
        connectivity,
        edgeIndex,
      );
      let touchesSecondary = false;
      for (const adjacentFace of adjacentFaces) {
        if (secondary.faces.has(adjacentFace)) {
          touchesSecondary = true;
          break;
        }
      }
      if (!touchesSecondary) continue;

      const [v0, v1] = edgeVertices(primaryMesh.topology.edges, edgeIndex);
      const a = transformLocalPoint(
        localVertex(primaryMesh.positions, v0),
        primaryMesh.worldMatrix,
      );
      const b = transformLocalPoint(
        localVertex(primaryMesh.positions, v1),
        primaryMesh.worldMatrix,
      );
      const score = pointSegmentDistanceSq(secondary.hit.worldPosition, a, b);
      if (score < bestScore) {
        bestScore = score;
        best = [a, b];
      }
    }
  }

  return best;
}

function regionMesh(region: SurfaceRegion) {
  return region.hit.face?.mesh ?? null;
}

function commonRegionMesh(
  regions: readonly SurfaceRegion[],
): SurfaceMesh | null {
  const first = regions[0] ? regionMesh(regions[0]) : null;
  if (!first) return null;
  for (let i = 1; i < regions.length; i += 1) {
    const mesh = regionMesh(regions[i]!);
    if (
      !mesh ||
      mesh.topology !== first.topology ||
      mesh.positions !== first.positions
    ) {
      return null;
    }
  }
  return first;
}

function regionVertexRepresentatives(
  region: SurfaceRegion,
  connectivity: SurfaceRegionConnectivity,
): Set<number> | null {
  const mesh = regionMesh(region);
  if (!mesh) return null;
  const out = new Set<number>();
  for (const faceIndex of region.faces) {
    const start = mesh.topology.offsets[faceIndex];
    const end = mesh.topology.offsets[faceIndex + 1];
    if (start === undefined || end === undefined || end <= start) continue;
    for (let c = start; c < end; c += 1) {
      const vi = mesh.topology.corners[c]!;
      out.add(connectivity.weldReps?.[vi] ?? vi);
    }
  }
  return out;
}

function commonRegionVertex(regions: readonly SurfaceRegion[]): Vec3 | null {
  const mesh = commonRegionMesh(regions);
  if (!mesh) return null;
  const connectivity = getSurfaceRegionConnectivity(mesh);

  const sets = regions.map((region) =>
    regionVertexRepresentatives(region, connectivity),
  );
  if (sets.some((set) => !set || set.size === 0)) return null;

  const common = new Set(sets[0]!);
  for (let i = 1; i < sets.length; i += 1) {
    for (const vi of common) {
      if (!sets[i]!.has(vi)) common.delete(vi);
    }
  }
  if (common.size === 0) return null;

  let target: Vec3 = [0, 0, 0];
  for (const region of regions) {
    target = add(target, region.hit.worldPosition);
  }
  target = scale(target, 1 / regions.length);

  let best: Vec3 | null = null;
  let bestScore = Number.POSITIVE_INFINITY;
  const candidates = new Set<number>();
  for (const region of regions) {
    for (const faceIndex of region.faces) {
      const start = mesh.topology.offsets[faceIndex]!;
      const end = mesh.topology.offsets[faceIndex + 1]!;
      for (let c = start; c < end; c += 1) {
        const vi = mesh.topology.corners[c]!;
        const rep = connectivity.weldReps?.[vi] ?? vi;
        if (common.has(rep)) candidates.add(vi);
      }
    }
  }

  for (const vi of candidates) {
    const point = transformLocalPoint(
      localVertex(mesh.positions, vi),
      mesh.worldMatrix,
    );
    const score = distanceSq(point, target);
    if (score < bestScore) {
      bestScore = score;
      best = point;
    }
  }
  return best;
}

function joinedSurfaceRegionEdge(
  primary: ViewportSurfaceHit,
  secondary: ViewportSurfaceHit,
): readonly [Vec3, Vec3] | null {
  const primaryRegion = buildCoplanarRegion(primary);
  const secondaryRegion = buildCoplanarRegion(secondary);
  if (!primaryRegion || !secondaryRegion) return null;
  return sharedRegionEdge(primaryRegion, secondaryRegion);
}

export function joinedSurfaceEdge(
  primary: ViewportSurfaceHit,
  secondary: ViewportSurfaceHit,
): readonly [Vec3, Vec3] | null {
  if (primary.objectId !== secondary.objectId) return null;

  const regionEdge = joinedSurfaceRegionEdge(primary, secondary);
  if (regionEdge) return regionEdge;

  const primaryVertices = primary.face?.vertices ?? primary.triangle?.vertices;
  const secondaryVertices =
    secondary.face?.vertices ?? secondary.triangle?.vertices;
  if (!primaryVertices || !secondaryVertices) return null;

  const eps =
    Math.max(verticesScale(primaryVertices), verticesScale(secondaryVertices)) *
    EDGE_EPSILON;
  const epsSq = eps * eps;
  const primaryIndices = new Set<number>();

  for (let i = 0; i < primaryVertices.length; i += 1) {
    for (let j = 0; j < secondaryVertices.length; j += 1) {
      if (distanceSq(primaryVertices[i]!, secondaryVertices[j]!) <= epsSq) {
        primaryIndices.add(i);
      }
    }
  }

  if (primaryIndices.size !== 2) return null;
  const indices = [...primaryIndices];
  return [primaryVertices[indices[0]!]!, primaryVertices[indices[1]!]!];
}

export function joinedSurfaceBasis(
  primary: ViewportSurfaceHit,
  secondary: ViewportSurfaceHit,
): FaceBasis | null {
  if (primary.objectId !== secondary.objectId) return null;
  if (
    primary.face &&
    secondary.face &&
    primary.face.index === secondary.face.index
  ) {
    return null;
  }

  const n1 = normalize(surfaceHitFaceNormal(primary) ?? primary.normal);
  const n2 = normalize(surfaceHitFaceNormal(secondary) ?? secondary.normal);
  if (!n1 || !n2) return null;
  if (Math.abs(dot(n1, n2)) > COPLANAR_NORMAL_DOT) return null;

  const edge = joinedSurfaceEdge(primary, secondary);
  if (!edge) return null;

  const normal = normalize(add(n1, n2));
  if (!normal) return null;

  return computeWorldUpFaceBasis({
    worldPosition: scale(add(edge[0], edge[1]), 0.5),
    normal,
  });
}

export function joinedSurfaceBasisForHits(
  hits: readonly ViewportSurfaceHit[],
): FaceBasis | null {
  if (hits.length === 2) return joinedSurfaceBasis(hits[0]!, hits[1]!);
  if (hits.length !== 3) return null;

  const objectId = hits[0]!.objectId;
  if (hits.some((hit) => hit.objectId !== objectId)) return null;

  const normals: Vec3[] = [];
  for (const hit of hits) {
    const normal = normalize(surfaceHitFaceNormal(hit) ?? hit.normal);
    if (!normal) return null;
    normals.push(normal);
  }

  for (let i = 0; i < normals.length; i += 1) {
    for (let j = i + 1; j < normals.length; j += 1) {
      if (Math.abs(dot(normals[i]!, normals[j]!)) > COPLANAR_NORMAL_DOT) {
        return null;
      }
    }
  }

  const regions = hits.map(buildCoplanarRegion);
  if (regions.some((region) => region === null)) return null;
  const surfaceRegions = regions as SurfaceRegion[];

  const origin = commonRegionVertex(surfaceRegions);
  if (!origin) return null;

  let normal: Vec3 = [0, 0, 0];
  for (const n of normals) normal = add(normal, n);
  const averagedNormal = normalize(normal);
  if (!averagedNormal) return null;

  const hasSharedEdge =
    sharedRegionEdge(surfaceRegions[0]!, surfaceRegions[1]!) ??
    sharedRegionEdge(surfaceRegions[0]!, surfaceRegions[2]!) ??
    sharedRegionEdge(surfaceRegions[1]!, surfaceRegions[2]!);
  if (!hasSharedEdge) return null;

  return computeWorldUpFaceBasis({
    worldPosition: origin,
    normal: averagedNormal,
  });
}
